/**
 * Session-level perf tags applied via Sentry.setTag at init time. Both
 * are derived once per page load — they don't change within a session.
 *
 * `perf.tz_region` is a coarse geography proxy. We don't enable
 * sendDefaultPii in this codebase, so Sentry's server cannot derive
 * geo.country_code from the client IP. The timezone region is the
 * smallest leak-free fallback: it groups users into ~12 buckets
 * (Asia, America, Europe, Australia, Africa, Pacific, Atlantic,
 * Indian, Antarctica, Etc, GMT-like, unknown), enough to investigate
 * whether the slow Register-span tail is concentrated in distant
 * regions from the europe-west2 origin.
 *
 * `perf.network_class` reports the browser's Network Information API
 * estimate of the connection class (`4g`, `3g`, `2g`, `slow-2g`).
 * It's a heuristic — the browser updates it from recent traffic
 * — but it discriminates "wired desktop" from "mobile 3G" cleanly
 * enough to test whether slow spans correlate with slow networks
 * independent of geography.
 *
 * Returns an empty object when the relevant globals are unavailable
 * (SSR, tests without mocks, browsers without Network Information API).
 */
export function getSessionTags(): Record<string, string> {
  const tags: Record<string, string> = {};

  if (typeof Intl !== 'undefined') {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (typeof tz === 'string' && tz.length > 0) {
        // tz is non-empty here, so split('/')[0] is also non-empty.
        tags['perf.tz_region'] = tz.split('/')[0];
      }
    } catch {
      // Intl can throw on exotic platforms; ignore.
    }
  }

  // navigator.connection is non-standard; only Chromium-family browsers
  // expose it today. Use a structural cast since the type isn't in lib.dom.
  if (typeof navigator !== 'undefined') {
    const conn = (
      navigator as Navigator & { connection?: { effectiveType?: string } }
    ).connection;
    const effectiveType = conn?.effectiveType;
    if (typeof effectiveType === 'string' && effectiveType.length > 0) {
      tags['perf.network_class'] = effectiveType;
    }
  }

  return tags;
}
